import type { RequiredUiBlockFamilyLiveSwapReceipt } from "@cogo/schema/required-ui-block-admission"

import {
  buildRequiredUiBlockFamilyCertificationPlan,
  REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES,
} from "./required-ui-block-family-certification"
import type {
  RequiredUiBlockCertificationArtifactSubjectObservation,
  RequiredUiBlockCertificationSubjectObservation,
} from "./required-ui-block-family-certification-protocol"
import {
  buildRequiredUiBlockFamilyLiveSwapReceipt,
  type RequiredUiBlockFamilyBrowserObservation,
  type RequiredUiBlockFamilyEvidenceSource,
  type RequiredUiBlockFamilyEvidenceSourcesV1,
} from "./required-ui-block-family-live-swap"

export interface RequiredUiBlockCertificationConditionEvidence {
  conditionId: string
  status: "completed" | "not-run"
  blockers: string[]
  consoleErrors: string[]
  subject?: RequiredUiBlockCertificationSubjectObservation
}

export interface RequiredUiBlockCertificationFamilyRunEvidence {
  patternId: string
  familyId: string
  variantId: string
  sourceContentHash: `sha256:${string}`
  preflight: {
    g1Facts: Record<string, unknown>
    g2Facts: Record<string, unknown>
    blockers: string[]
    admissionAuthorityBlockers: string[]
  }
  baselineComparisons: Record<string, {
    status: "matched" | "mismatch" | "missing" | "error"
    differentPixels: number
    totalPixels: number
    blocker?: string
  }>
  conditions: RequiredUiBlockCertificationConditionEvidence[]
}

export interface RequiredUiBlockFamilyCertificationMatrix {
  schemaVersion: "cogo.required-ui-block-family-certification-matrix.v1"
  patternId: string
  familyId: string
  variantId: string
  sourceContentHash: `sha256:${string}`
  expectedObservationCount: 36
  completedObservationCount: number
  minimumQualityScore: number
  qualityFloor: 85
  qualityPass: boolean
  conditions: RequiredUiBlockCertificationConditionEvidence[]
  blockers: string[]
}

export interface RequiredUiBlockFamilyCertificationReduction {
  matrix: RequiredUiBlockFamilyCertificationMatrix
  g3: RequiredUiBlockFamilyLiveSwapReceipt
  g3Complete: boolean
  blockers: string[]
}

export interface RequiredUiBlockFamilyCertificationAggregate {
  schemaVersion: "cogo.required-ui-block-family-certification-reduction.v1"
  expectedFamilyCount: 26
  expectedArtifactCount: 52
  expectedObservationCount: 936
  completedObservationCount: number
  g3CompleteCount: number
  qualityPassCount: number
  complete: boolean
  families: RequiredUiBlockFamilyCertificationReduction[]
  blockers: string[]
}

function stable(value: unknown): string {
  return JSON.stringify(value)
}

export function requiredUiBlockComputedProbeBlockers(
  artifactRef: string,
  key: string,
  values: readonly string[],
  expectedDistinctAcrossReleases = true,
): string[] {
  if (values.length !== 3 || values.some((value) => !value.trim())) {
    return [`computed-probe-missing:${artifactRef}:${key}`]
  }
  return !expectedDistinctAcrossReleases || new Set(values).size > 1
    ? []
    : [`computed-probe-not-distinct:${artifactRef}:${key}`]
}

export function requiredUiBlockObservedVisualProbes(
  observation: Pick<RequiredUiBlockCertificationArtifactSubjectObservation, "visualProbes"> | undefined,
): Record<string, string> {
  return { ...(observation?.visualProbes ?? {}) }
}

export function requiredUiBlockFocusContractRestored(
  focusTargets: readonly { artifactRef: string; elementId: string }[],
  artifactRef: string,
  focusedElementId: string | null | undefined,
): boolean {
  const target = focusTargets.find((candidate) => candidate.artifactRef === artifactRef)
  return target ? focusedElementId === target.elementId : true
}

function sourceForPattern(
  sources: readonly RequiredUiBlockFamilyEvidenceSourcesV1[],
  patternId: string,
): { source: RequiredUiBlockFamilyEvidenceSourcesV1; family: RequiredUiBlockFamilyEvidenceSource } {
  for (const source of sources) {
    const family = source.families.find((candidate) => candidate.patternId === patternId)
    if (family) return { source, family }
  }
  throw new Error(`certification-source-family-missing:${patternId}`)
}

function observationById(
  evidence: RequiredUiBlockCertificationFamilyRunEvidence,
): Map<string, RequiredUiBlockCertificationConditionEvidence> {
  return new Map(evidence.conditions.map((condition) => [condition.conditionId, condition]))
}

function requiredCondition(
  observations: Map<string, RequiredUiBlockCertificationConditionEvidence>,
  patternId: string,
  artifactRef: string,
  releaseId: string,
): RequiredUiBlockCertificationConditionEvidence | undefined {
  return observations.get(`${patternId}:${artifactRef}:${releaseId}:light:desktop`)
}

function observedArtifact(
  condition: RequiredUiBlockCertificationConditionEvidence | undefined,
  artifactRef: string,
): RequiredUiBlockCertificationArtifactSubjectObservation | undefined {
  return condition?.subject?.artifacts.find(
    (artifact) => artifact.artifactRef === artifactRef,
  )
}

function browserObservation(
  source: RequiredUiBlockFamilyEvidenceSourcesV1,
  family: RequiredUiBlockFamilyEvidenceSource,
  evidence: RequiredUiBlockCertificationFamilyRunEvidence,
): RequiredUiBlockFamilyBrowserObservation {
  const observations = observationById(evidence)
  return {
    patternId: family.patternId,
    familyId: family.familyId,
    variantId: family.variantId,
    evidenceSourceContentHash: source.contentHash,
    rendererContract: source.rendererContract,
    artifacts: family.canonicalArtifacts.map((artifact) => ({
      artifactRef: artifact.artifactRef,
      bundleFiles: artifact.bundleFiles,
      baselineSourceSnapshotContentHash: artifact.baselineSource.snapshotContentHash,
      baselineDifferentPixels:
        evidence.baselineComparisons[artifact.artifactRef]?.differentPixels ?? 1,
      baselineTotalPixels:
        evidence.baselineComparisons[artifact.artifactRef]?.totalPixels ?? 1,
      releases: REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES.map((releaseId, releaseIndex) => {
        const condition = requiredCondition(
          observations,
          family.patternId,
          artifact.artifactRef,
          releaseId,
        )
        const artifactObservation = observedArtifact(condition, artifact.artifactRef)
        const release = source.releaseSet.find((candidate) => candidate.releaseId === releaseId)
        return {
          releaseId,
          releaseContentHash: (release?.contentHash ?? `sha256:${"0".repeat(64)}`) as `sha256:${string}`,
          releaseEpoch: releaseIndex + 1,
          mountId: artifactObservation?.mountId ?? "",
          rendererNodeIdentity: artifactObservation?.rendererNodeIdentity ?? "",
          blockArtifactHash: artifactObservation?.artifactHash ?? artifact.artifactHash,
          structuralFingerprint: artifactObservation?.structuralFingerprint ?? "",
          interactionState: {
            controls: artifactObservation?.controls ?? [],
            scrollPositions: artifactObservation?.scrollPositions ?? {},
          },
          focusedElementId: artifactObservation?.focusedElementId ?? null,
          focusContractuallyRestored: requiredUiBlockFocusContractRestored(
            family.sourceContracts.focusTargets,
            artifact.artifactRef,
            artifactObservation?.focusedElementId,
          ),
          actionFlowTrace: artifactObservation?.actionFlowTrace ?? [],
          visualProbes: requiredUiBlockObservedVisualProbes(artifactObservation),
          resolvedDesignRoles: artifactObservation?.resolvedDesignRoles ?? {},
          unresolvedDesignAuthorityCount: artifactObservation?.unresolvedDesignAuthorityCount ?? 1,
          unknownElementCount: artifactObservation?.unknownElementCount ?? 1,
          consoleErrorCount: condition?.consoleErrors.length ?? 1,
        }
      }),
    })),
  }
}

export function reduceRequiredUiBlockFamilyCertification(
  sources: readonly RequiredUiBlockFamilyEvidenceSourcesV1[],
  evidence: RequiredUiBlockCertificationFamilyRunEvidence,
): RequiredUiBlockFamilyCertificationReduction {
  const { source, family } = sourceForPattern(sources, evidence.patternId)
  const expected = buildRequiredUiBlockFamilyCertificationPlan(sources)
    .families.find((candidate) => candidate.patternId === family.patternId)
    ?.canonicalArtifacts.flatMap((artifact) => artifact.conditions.map((condition) => condition.conditionId)) ?? []
  const actualIds = evidence.conditions.map((condition) => condition.conditionId)
  const blockers = [
    ...(expected.length === 36 ? [] : [`expected-observation-count:${expected.length}/36`]),
    ...(actualIds.length === 36 ? [] : [`observation-count:${actualIds.length}/36`]),
    ...(new Set(actualIds).size === actualIds.length ? [] : ["observation-id-duplicate"]),
    ...expected.filter((identity) => !actualIds.includes(identity)).map((identity) => `observation-missing:${identity}`),
    ...actualIds.filter((identity) => !expected.includes(identity)).map((identity) => `observation-unknown:${identity}`),
    ...evidence.conditions.flatMap((condition) =>
      condition.status === "completed" ? [] : [`condition-not-run:${condition.conditionId}`],
    ),
  ]
  const observations = evidence.conditions.flatMap((condition) =>
    condition.subject?.artifacts ?? [],
  )
  const qualityScores = observations.map((observation) => observation.quality.score)
  const minimumQualityScore = qualityScores.length > 0 ? Math.min(...qualityScores) : 0
  const qualityBlockers = evidence.conditions.flatMap((condition) =>
    condition.subject?.artifacts.flatMap((artifact) =>
      artifact.quality.blockers.map((blocker) => `${condition.conditionId}:${blocker}`),
    ) ?? [],
  )
  blockers.push(...qualityBlockers)

  for (const artifact of family.canonicalArtifacts) {
    const artifactConditions = evidence.conditions.filter((condition) =>
      condition.conditionId.startsWith(`${family.patternId}:${artifact.artifactRef}:`),
    )
    const snapshots = artifactConditions.flatMap((condition) => {
      const snapshot = observedArtifact(condition, artifact.artifactRef)
      return snapshot ? [snapshot] : []
    })
    for (const [label, values] of [
      ["mount", snapshots.map((snapshot) => snapshot.mountId)],
      ["renderer-node", snapshots.map((snapshot) => snapshot.rendererNodeIdentity)],
      ["structure", snapshots.map((snapshot) => snapshot.structuralFingerprint)],
      ["controls", snapshots.map((snapshot) => stable(snapshot.controls))],
      ["focus", snapshots.map((snapshot) => snapshot.focusedElementId ?? "contract:none")],
      ["scroll", snapshots.map((snapshot) => stable(snapshot.scrollPositions))],
      ["action-flow", snapshots.map((snapshot) => stable(snapshot.actionFlowTrace))],
    ] as const) {
      if (values.length !== 18 || new Set(values).size !== 1) blockers.push(`${label}-changed:${artifact.artifactRef}`)
    }
    const releaseSignatures = REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES.map((releaseId) =>
      stable(observedArtifact(
        artifactConditions.find((condition) => condition.conditionId.includes(`:${releaseId}:light:desktop`)),
        artifact.artifactRef,
      )?.resolvedDesignRoles ?? {}),
    )
    if (new Set(releaseSignatures).size !== 3) blockers.push(`release-signature-not-distinct:${artifact.artifactRef}`)
    const schemeSignatures = ["light", "dark"].map((scheme) =>
      stable(observedArtifact(
        artifactConditions.find((condition) => condition.conditionId.includes(`:baseline.v1:${scheme}:desktop`)),
        artifact.artifactRef,
      )?.visualSignature ?? ""),
    )
    if (new Set(schemeSignatures).size !== 2) blockers.push(`scheme-signature-not-distinct:${artifact.artifactRef}`)
    for (const probe of family.sourceContracts.visualProbes.filter(
      (candidate) => candidate.artifactRef === artifact.artifactRef,
    )) {
      const key = `${probe.elementId}:${probe.property}`
      const actualValues = REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES.map((releaseId) =>
        observedArtifact(
          artifactConditions.find((condition) =>
            condition.conditionId.includes(`:${releaseId}:light:desktop`)),
          artifact.artifactRef,
        )?.visualProbes[key]?.trim() ?? "",
      )
      blockers.push(...requiredUiBlockComputedProbeBlockers(
        artifact.artifactRef,
        key,
        actualValues,
        probe.expectedDistinctAcrossReleases !== false,
      ))
    }
  }

  const matrix: RequiredUiBlockFamilyCertificationMatrix = {
    schemaVersion: "cogo.required-ui-block-family-certification-matrix.v1",
    patternId: family.patternId,
    familyId: family.familyId,
    variantId: family.variantId,
    sourceContentHash: source.contentHash,
    expectedObservationCount: 36,
    completedObservationCount: evidence.conditions.filter((condition) => condition.status === "completed").length,
    minimumQualityScore,
    qualityFloor: 85,
    qualityPass: minimumQualityScore >= 85 && qualityBlockers.length === 0,
    conditions: evidence.conditions,
    blockers: [...new Set(blockers)].sort(),
  }
  const g3 = buildRequiredUiBlockFamilyLiveSwapReceipt(
    source,
    family,
    browserObservation(source, family, evidence),
  )
  const g3Complete = g3.blockers.length === 0
  const reductionBlockers = [
    ...matrix.blockers,
    ...g3.blockers.map((blocker) => `G3:${blocker}`),
    ...evidence.preflight.blockers.map((blocker) => `preflight:${blocker}`),
    ...evidence.preflight.admissionAuthorityBlockers.map((blocker) => `authority:${blocker}`),
  ]
  return { matrix, g3, g3Complete, blockers: [...new Set(reductionBlockers)].sort() }
}

export function aggregateRequiredUiBlockFamilyCertification(
  sources: readonly RequiredUiBlockFamilyEvidenceSourcesV1[],
  evidence: readonly RequiredUiBlockCertificationFamilyRunEvidence[],
): RequiredUiBlockFamilyCertificationAggregate {
  const families = evidence.map((family) => reduceRequiredUiBlockFamilyCertification(sources, family))
  const completedObservationCount = families.reduce(
    (sum, family) => sum + family.matrix.completedObservationCount,
    0,
  )
  const blockers = [
    ...(families.length === 26 ? [] : [`family-count:${families.length}/26`]),
    ...(completedObservationCount === 936 ? [] : [`observation-count:${completedObservationCount}/936`]),
    ...families.flatMap((family) => family.blockers.map((blocker) => `${family.matrix.patternId}:${blocker}`)),
  ]
  return {
    schemaVersion: "cogo.required-ui-block-family-certification-reduction.v1",
    expectedFamilyCount: 26,
    expectedArtifactCount: 52,
    expectedObservationCount: 936,
    completedObservationCount,
    g3CompleteCount: families.filter((family) => family.g3Complete).length,
    qualityPassCount: families.filter((family) => family.matrix.qualityPass).length,
    complete: families.length === 26 && completedObservationCount === 936 && blockers.length === 0,
    families,
    blockers: [...new Set(blockers)].sort(),
  }
}
